import React, {FC, useCallback, DragEvent, SyntheticEvent, useRef} from 'react';
import {useFormikContext} from 'formik';
import {IWithChildren} from 'models/common';
import {DraggableArea} from 'components-lib';
import {useFileSelectionLoading} from 'hooks';

interface IUploadDraggableAreaProps extends IWithChildren {
  name: string;
}

export const UploadDraggableArea: FC<IUploadDraggableAreaProps> = ({children, name}) => {
  const {setFieldValue, setFieldTouched} = useFormikContext();
  const fileSelectionLoading = useFileSelectionLoading();
  const dragDepthRef = useRef(0);
  const isCommittingDropRef = useRef(false);

  const handleFileDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      isCommittingDropRef.current = true;
      dragDepthRef.current = 0;

      const newFile = event.dataTransfer.files[0];
      if (!newFile) {
        isCommittingDropRef.current = false;
        fileSelectionLoading?.clearLoading();
        return;
      }

      setFieldTouched(name, true, true);

      if (fileSelectionLoading) {
        fileSelectionLoading.applySelectedFile(newFile);
      } else {
        setFieldValue(name, newFile);
      }

      isCommittingDropRef.current = false;
    },
    [fileSelectionLoading, name, setFieldTouched, setFieldValue]
  );

  const handleDragOver = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      dragDepthRef.current += 1;

      if (dragDepthRef.current === 1 && event.dataTransfer?.types?.includes('Files')) {
        fileSelectionLoading?.startLoading();
      }
    },
    [fileSelectionLoading]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

      if (dragDepthRef.current === 0 && !isCommittingDropRef.current) {
        fileSelectionLoading?.clearLoading();
      }
    },
    [fileSelectionLoading]
  );

  return (
    <div onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
      <DraggableArea onDrop={handleFileDrop} onDragOver={handleDragOver}>
        {children}
      </DraggableArea>
    </div>
  );
};
